import React, { useState, useMemo } from 'react';
import { Table, Checkbox, Button, Icon, Image, Label, Popup, Input, Message, Modal } from 'semantic-ui-react';
import images from '../../images';
import FirstCopyCoachmark from '../stock/FirstCopyCoachmark';
import { getReportableStock, getSaleUnitQuantity } from './packStock';

const marketplaceDisplayNames = {
  mercado_libre: 'Mercado Libre',
  tienda_nube: 'Tienda Nube',
};

const formatCurrency = (value, options = {}) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: options.currency || 'ARS',
      maximumFractionDigits: 0,
    }).format(amount);
};

const formatFinancialCurrency = (value) => {
  return formatCurrency(value, { currency: 'ARS' });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const hasInternalCostLoaded = (financial) => financial?.hasInternalCost === true;

const renderMarginText = (financial) => {
    if (!financial) return '-';
    if (!hasInternalCostLoaded(financial)) return 'Sin costo interno';
    return formatFinancialCurrency(financial.marginAmount);
};

const formatMarginPercent = (financial) => {
  if (!financial || !hasInternalCostLoaded(financial)) {
    return '-';
  }
  const costAmount = Number(financial.costAmount || 0);
  if (!Number.isFinite(costAmount) || costAmount <= 0) {
    return '-';
  }
  const percent = (Number(financial.marginAmount || 0) / costAmount) * 100;
  if (!Number.isFinite(percent)) {
    return '-';
  }
  return `${percent.toFixed(2)}%`;
};

const extractApiErrorData = (error) => error?.response?.data || {};
const isSkuConfirmationError = (error) =>
  [
    'SHARED_ITEM_SPLIT_REQUIRES_CONFIRMATION',
    'EXISTING_SKU_REBIND_REQUIRES_CONFIRMATION',
  ].includes(extractApiErrorData(error)?.code || '');
const isVariationPriceConfirmationError = (error) =>
  extractApiErrorData(error)?.code === 'ML_VARIATION_PRICE_CHANGE_REQUIRES_CONFIRMATION';

const TableContentComponent = ({
  args: {
    filteredItems,
    selectedItems,
    setSelectedItems,
    selectedListingIds,
    setSelectedListingIds,
    publishSelectionMode,
    firstCopyGuidanceActive,
    onFirstCopyGuidanceDismiss,
    autoExpandListingIds,
    handleEditClick,
    handleViewClick,
    rowEditEnabled,
    handleInlineListingUpdate,
    handleDeleteListing,
    listingFinancialsById,
    listingFinancialsLoadingById,
    handleViewFinancialDetail,
    handleRetryFinancials,
    onViewKit,
    bulkPriceUpdateEnabled,
    packsEnabled = false,
  },
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [activeCell, setActiveCell] = useState(null);
  const [draftValues, setDraftValues] = useState({});
  const [savingCells, setSavingCells] = useState({});
  const [savingListings, setSavingListings] = useState({});
  const [savingItems, setSavingItems] = useState({});
  const [pendingSkuSplit, setPendingSkuSplit] = useState(null);
  const [pendingVariationPriceChange, setPendingVariationPriceChange] = useState(null);
  const [pendingDeleteListing, setPendingDeleteListing] = useState(null);
  const [deletingListingId, setDeletingListingId] = useState(null);
  const selectedListingIdSet = useMemo(
    () => new Set((selectedListingIds || []).map((value) => String(value || '').trim()).filter(Boolean)),
    [selectedListingIds],
  );

  const getItemListingIds = (item) =>
    (item?.listings || [])
      .map((listing) => String(listing?.id || '').trim())
      .filter(Boolean);

  // Después de copiar una publicación a otro canal sólo quedan abiertas las filas
  // de los SKUs copiados, para que se vea la publicación nueva sin desplegar todo.
  const autoExpandListingIdSet = useMemo(
    () =>
      new Set(
        (autoExpandListingIds || []).map((value) => String(value || '').trim()).filter(Boolean),
      ),
    [autoExpandListingIds],
  );

  const isAutoExpandedItem = (item) =>
    autoExpandListingIdSet.size > 0 &&
    getItemListingIds(item).some((listingId) => autoExpandListingIdSet.has(listingId));

  const resolveExpanded = (item) =>
    expandedRows[item?.id] ?? (publishSelectionMode || isAutoExpandedItem(item));

  const handleCheckboxChange = (item) => {
    const itemId = item?.id;
    if (!itemId) return;
    const listingIds = getItemListingIds(item);
    const listingIdSet = new Set(listingIds);

    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.includes(itemId)
        ? prevSelectedItems.filter((id) => id !== itemId)
        : [...prevSelectedItems, itemId],
    );

    setSelectedListingIds((prevSelectedListingIds) => {
      const itemSelected = selectedItems.includes(itemId);

      if (itemSelected) {
        return (prevSelectedListingIds || []).filter((listingId) => !listingIdSet.has(String(listingId || '').trim()));
      }

      return Array.from(new Set([...(prevSelectedListingIds || []), ...listingIds]));
    });
  };

  const handleListingCheckboxChange = (listingId) => {
    const normalizedId = String(listingId || '').trim();
    if (!normalizedId) return;

    setSelectedListingIds((prevSelectedListingIds) =>
      prevSelectedListingIds.includes(normalizedId)
        ? prevSelectedListingIds.filter((id) => id !== normalizedId)
        : [...prevSelectedListingIds, normalizedId],
    );
  };

  const toggleExpanded = (item) => {
    const itemId = typeof item === 'string' ? item : item?.id;
    if (!itemId) return;
    const currentlyExpanded =
      typeof item === 'string'
        ? expandedRows[itemId] ?? publishSelectionMode
        : resolveExpanded(item);

    setExpandedRows((prev) => ({
      ...prev,
      // Selection mode opens rows by default, but each row can still be toggled.
      [itemId]: !currentlyExpanded,
    }));
  };

  const getItemMarketplaces = (item) =>
    Array.from(
      new Set(
        (item?.listings || [])
          .map((listing) => String(listing?.marketplace || '').trim())
          .filter(Boolean),
      ),
    );

  const getMarketplaceIcon = (marketplace) => (marketplace === 'mercado_libre' ? images.meliIcon : images.tnIcon);
  const formatPrice = (value) => Number(value || 0).toFixed(2);
  const cellKey = (listingId, field) => `${listingId}:${field}`;
  const detailGridTemplateColumns = bulkPriceUpdateEnabled === true
    ? '56px minmax(320px, 1.8fr) 110px minmax(118px, max-content) 88px 110px 96px 112px 88px 130px 120px 105px 130px 52px 76px'
    : '56px minmax(320px, 1.8fr) 110px minmax(118px, max-content) 88px 110px 96px 88px 88px 130px 76px';
  const listingToItemId = useMemo(() => {
    const map = {};
    (filteredItems || []).forEach((item) => {
      (item.listings || []).forEach((listing) => {
        if (listing?.id) {
          map[listing.id] = item.id;
        }
      });
    });
    return map;
  }, [filteredItems]);
  const getStockMode = (listing) => String(listing?.stockMode || 'standard');
  const isMlFullOnly = (listing) =>
    listing?.marketplace === 'mercado_libre' && getStockMode(listing) === 'full_only';
  const isMlFullFlex = (listing) =>
    listing?.marketplace === 'mercado_libre' && getStockMode(listing) === 'full_flex';
  const getSellerStock = (listing) =>
    Number(listing?.sellerStockQuantity ?? listing?.availableQuantity ?? 0);
  const getListingSaleUnitQuantity = (listing) => getSaleUnitQuantity(listing?.saleUnitQuantity);
  const getListingReportableStock = (listing) => {
    const supplied = Number(listing?.reportableStock);
    return Number.isFinite(supplied) && supplied >= 0
      ? Math.floor(supplied)
      : getReportableStock(getSellerStock(listing), getListingSaleUnitQuantity(listing));
  };
  const getMarketplaceStock = (listing) => {
    const candidate = Number(listing?.marketplaceStockQuantity);
    if (!Number.isFinite(candidate)) return null;
    return Math.max(0, Math.floor(candidate));
  };
  const getMarketplaceName = (marketplace) =>
    marketplaceDisplayNames[marketplace] || 'Canal';
  const getFullStock = (listing) => Number(listing?.fullStockQuantity || 0);
  const isKitListing = (listing) => listing?.kind === 'KIT';
  const canEditStock = (listing) =>
    !isKitListing(listing) && (listing?.stockEditable !== false || isMlFullOnly(listing));
  const hasStockDrift = (listing) => {
    if (isMlFullOnly(listing)) return false;
    const marketplaceStock = getMarketplaceStock(listing);
    if (marketplaceStock === null) return false;
    return marketplaceStock !== getListingReportableStock(listing);
  };

  const isEditingCell = (listingId, field) =>
    activeCell?.listingId === listingId && activeCell?.field === field;

  const getCurrentCellValue = (listing, field) => {
    if (field === 'price') return formatPrice(listing.price);
    if (field === 'availableQuantity') return String(getSellerStock(listing));
    return String(listing.sku || '');
  };

  const getDisplayValue = (listing, field) => {
    const key = cellKey(listing.id, field);
    if (draftValues[key] !== undefined) return draftValues[key];
    return getCurrentCellValue(listing, field);
  };

  const parseValue = (field, rawValue) => {
    if (field === 'sku') return String(rawValue || '').trim();
    if (field === 'availableQuantity') {
      const parsed = Number.parseInt(rawValue, 10);
      if (Number.isNaN(parsed) || parsed < 0) return null;
      return parsed;
    }
    if (field === 'price') {
      const parsed = Number.parseFloat(rawValue);
      if (!Number.isFinite(parsed) || parsed < 0) return null;
      return Number(parsed.toFixed(2));
    }
    return rawValue;
  };

  const getPayload = (field, parsedValue) => {
    if (field === 'sku') return { sku: parsedValue };
    if (field === 'availableQuantity') return { stock: parsedValue };
    if (field === 'price') return { price: parsedValue };
    return {};
  };

  const requestSkuSplitConfirmation = (listing, nextSku, errorData = {}) =>
    new Promise((resolve) => {
      setPendingSkuSplit({
        listing,
        nextSku,
        currentSku: errorData?.currentSku || listing?.sku || '',
        message: errorData?.message || '',
        code: errorData?.code || '',
        targetItemTitle: errorData?.targetItemTitle || '',
        resolve,
      });
    });

  const requestVariationPriceChangeConfirmation = (listing, nextPrice, errorData = {}) =>
    new Promise((resolve) => {
      setPendingVariationPriceChange({
        listing,
        nextPrice,
        variationCount: Number(errorData?.variationCount || 0),
        resolve,
      });
    });

  const beginEdit = (listing, field) => {
    const parentItemId = listingToItemId[listing.id];
    if (savingListings[listing.id] || (parentItemId && savingItems[parentItemId])) return;
    const key = cellKey(listing.id, field);
    setActiveCell({ listingId: listing.id, field });
    setDraftValues((prev) => ({
      ...prev,
      [key]: getDisplayValue(listing, field),
    }));
  };

  const onDraftChange = (listingId, field, value) => {
    const key = cellKey(listingId, field);
    setDraftValues((prev) => ({ ...prev, [key]: value }));
  };

  const commitCell = async (listing, field) => {
    const key = cellKey(listing.id, field);
    const rawValue = getDisplayValue(listing, field);
    const parsedValue = parseValue(field, rawValue);
    const currentParsed = parseValue(field, getCurrentCellValue(listing, field));

    setActiveCell(null);

    const isUnchanged = parsedValue === currentParsed;
    const forceStockUpdate = field === 'availableQuantity' && hasStockDrift(listing);
    const skipUnchanged = isUnchanged && !forceStockUpdate;
    if (parsedValue === null || parsedValue === '' || skipUnchanged) {
      setDraftValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    setSavingCells((prev) => ({ ...prev, [key]: true }));
    setSavingListings((prev) => ({ ...prev, [listing.id]: true }));
    const parentItemId = listingToItemId[listing.id];
    if (parentItemId) {
      setSavingItems((prev) => ({ ...prev, [parentItemId]: true }));
    }
    try {
      const payload = getPayload(field, parsedValue);

      try {
        await handleInlineListingUpdate(listing.id, payload);
      } catch (error) {
        if (field === 'sku' && isSkuConfirmationError(error)) {
          const confirmed = await requestSkuSplitConfirmation(
            listing,
            parsedValue,
            extractApiErrorData(error),
          );
          if (!confirmed) {
            setDraftValues((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
            return;
          }

          await handleInlineListingUpdate(listing.id, {
            ...payload,
            splitSharedItemWithZeroStock: true,
            confirmExistingSkuRebind: true,
          });
        } else if (field === 'price' && isVariationPriceConfirmationError(error)) {
          const confirmed = await requestVariationPriceChangeConfirmation(
            listing,
            parsedValue,
            extractApiErrorData(error),
          );
          if (!confirmed) {
            setDraftValues((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
            return;
          }

          await handleInlineListingUpdate(listing.id, {
            ...payload,
            confirmPriceForAllVariations: true,
          });
        } else {
          throw error;
        }
      }

      setDraftValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (error) {
      // Home already reports the failure to the person. Do not leave an unhandled
      // promise from a blur event if a stale row reaches a protected backend path.
      setDraftValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setSavingCells((prev) => ({ ...prev, [key]: false }));
      setSavingListings((prev) => ({ ...prev, [listing.id]: false }));
      if (parentItemId) {
        setSavingItems((prev) => ({ ...prev, [parentItemId]: false }));
      }
    }
  };

  const renderEditableCell = (itemId, listing, field, style = {}) => {
    const key = cellKey(listing.id, field);
    const isSaving = !!savingCells[key];
    const isRowSaving = !!savingListings[listing.id];
    const isSiblingSaving = !!savingItems[itemId];
    const isEditing = isEditingCell(listing.id, field);
    const itemEditEnabled = !!rowEditEnabled?.[itemId];
    const physicalStockLabelId = `physical-stock-unit-${listing.id}`;
    const fieldEditable =
      !((field === 'availableQuantity' && !canEditStock(listing)) ||
        (field === 'sku' && isKitListing(listing)));
    const readValue =
      field === 'availableQuantity' && isMlFullOnly(listing)
        ? '-'
        : field === 'price'
          ? `$${formatPrice(listing.price)}`
          : getDisplayValue(listing, field);

    if (!fieldEditable) {
      return (
        <div style={{ color: '#767676', ...style }}>
          {readValue}
        </div>
      );
    }

    if (itemEditEnabled || isEditing) {
      const input = (
        <Input
          size="mini"
          type={field === 'sku' ? 'text' : 'number'}
          step={field === 'price' ? '0.01' : '1'}
          min={field === 'sku' ? undefined : 0}
          value={getDisplayValue(listing, field)}
          onChange={(e) => onDraftChange(listing.id, field, e.target.value)}
          onBlur={() => commitCell(listing, field)}
          disabled={isSaving || isRowSaving || isSiblingSaving}
          autoFocus={!itemEditEnabled}
          input={
            field === 'availableQuantity' && getListingSaleUnitQuantity(listing) > 1
              ? { 'aria-describedby': physicalStockLabelId }
              : undefined
          }
          style={{ width: '100%', ...style }}
        />
      );
      if (field === 'availableQuantity' && getListingSaleUnitQuantity(listing) > 1) {
        return (
          <div style={{ display: 'grid', gap: 3 }}>
            {input}
            <span id={physicalStockLabelId} style={{ color: '#6B7280', fontSize: 11 }}>
              unidades físicas
            </span>
          </div>
        );
      }
      return input;
    }

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => beginEdit(listing, field)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') beginEdit(listing, field);
        }}
        style={{
          cursor: isSaving || isRowSaving || isSiblingSaving ? 'default' : 'text',
          pointerEvents: isSaving || isRowSaving || isSiblingSaving ? 'none' : 'auto',
          color: '#1b1c1d',
          ...style,
        }}
      >
        {readValue}
      </div>
    );
  };

  const renderReceivedCell = (listing) => {
    if (bulkPriceUpdateEnabled !== true) {
      return null;
    }
    if (listing.marketplace !== 'mercado_libre') {
      return <span style={{ color: '#777' }}>-</span>;
    }

    const financial = listingFinancialsById?.[listing.id];
    if (!financial?.hasSnapshot && listingFinancialsLoadingById?.[listing.id]) {
      return (
        <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="spinner" loading />
          Calculando
        </div>
      );
    }

    if (!financial?.hasSnapshot) {
      return <span style={{ color: '#777' }}>-</span>;
    }

    return (
      <span style={{ fontWeight: 700 }}>
        {formatFinancialCurrency(financial.receivedAmount)}
        {financial.freshness === 'stale' ? (
          <Popup content="Estimación guardada pendiente de actualización" trigger={<Icon name="clock outline" color="orange" style={{ marginLeft: 5 }} />} />
        ) : null}
      </span>
    );
  };

  const renderMarginCell = (listing) => {
    if (bulkPriceUpdateEnabled !== true) {
      return null;
    }
    if (listing.marketplace !== 'mercado_libre') {
      return <span style={{ color: '#777' }}>-</span>;
    }

    const financial = listingFinancialsById?.[listing.id];
    if (!financial?.hasSnapshot && listingFinancialsLoadingById?.[listing.id]) {
      return <span style={{ color: '#666' }}>Calculando</span>;
    }

    if (!financial?.hasSnapshot) {
      return <span style={{ color: '#777' }}>-</span>;
    }

    if (!hasInternalCostLoaded(financial)) {
      return <span style={{ color: '#777' }}>-</span>;
    }

    const isNegative = Number(financial.marginAmount || 0) < 0;
    return (
      <span style={{ color: isNegative ? '#a33333' : '#1b7f3b', fontWeight: 700 }}>
        {formatFinancialCurrency(financial.marginAmount)}
      </span>
    );
  };

  const renderMarginPercentCell = (listing) => {
    if (bulkPriceUpdateEnabled !== true) {
      return null;
    }
    if (listing.marketplace !== 'mercado_libre') {
      return <span style={{ color: '#777' }}>-</span>;
    }

    const financial = listingFinancialsById?.[listing.id];
    if (!financial?.hasSnapshot && listingFinancialsLoadingById?.[listing.id]) {
      return <span style={{ color: '#666' }}>Calculando</span>;
    }

    if (!financial?.hasSnapshot || !hasInternalCostLoaded(financial)) {
      return <span style={{ color: '#777' }}>-</span>;
    }

    const isNegative = Number(financial.marginAmount || 0) < 0;
    return (
      <span style={{ color: isNegative ? '#a33333' : '#1b7f3b', fontWeight: 700 }}>
        {formatMarginPercent(financial)}
      </span>
    );
  };

  const renderFinancialDetailCell = (listing) => {
    if (bulkPriceUpdateEnabled !== true) {
      return null;
    }
    if (listing.marketplace !== 'mercado_libre') {
      return <span style={{ color: '#777' }}>-</span>;
    }

    const financial = listingFinancialsById?.[listing.id];
    if (!financial?.hasSnapshot) {
      if (financial?.message && typeof handleRetryFinancials === 'function') {
        const retryAfter = Date.parse(String(financial.retryAfter || ''));
        const retryIsDeferred = Number.isFinite(retryAfter) && retryAfter > Date.now();
        return (
          <Button
            basic
            compact
            icon="refresh"
            size="mini"
            disabled={retryIsDeferred}
            onClick={retryIsDeferred ? undefined : handleRetryFinancials}
            title={retryIsDeferred ? 'Podés reintentar en unos minutos' : 'Reintentar cálculo financiero'}
          />
        );
      }
      return <span style={{ color: '#777' }}>-</span>;
    }

    return (
      <Button
        basic
        compact
        icon="info circle"
        size="mini"
        onClick={() => handleViewFinancialDetail(listing, financial)}
        title="Ver detalle financiero"
      />
    );
  };

  const openDeleteWarning = (listing) => {
    if (!listing?.id || deletingListingId) return;
    setPendingDeleteListing(listing);
  };

  const confirmDeleteListing = async () => {
    const listing = pendingDeleteListing;
    if (!listing?.id || typeof handleDeleteListing !== 'function') {
      setPendingDeleteListing(null);
      return;
    }

    setDeletingListingId(listing.id);
    try {
      await handleDeleteListing(listing.id);
      setPendingDeleteListing(null);
    } finally {
      setDeletingListingId(null);
    }
  };

  const renderListingActionCell = (item, listing) => {
    const kitListing = item.kind === 'KIT' || listing.kind === 'KIT';
    const viewLabel = kitListing
      ? `Ver detalle del kit ${listing.title || item.title || ''}`
      : `Ver información de la publicación ${listing.title || item.title || ''}`;

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 0 }}>
        <Button
          circular
          color="olive"
          size="mini"
          icon="eye"
          aria-label={viewLabel}
          title={viewLabel}
          style={{ margin: 0 }}
          onClick={() => {
            if (kitListing) {
              onViewKit?.(listing.itemId || item.id);
              return;
            }
            handleViewClick(listing);
          }}
        />
        <Button
          basic
          circular
          icon="trash"
          size="mini"
          color="red"
          onClick={() => openDeleteWarning(listing)}
          disabled={!!deletingListingId}
          title="Borrar listing de MercuryCom"
          style={{ margin: 0 }}
        />
      </div>
    );
  };

  const renderStaticCell = (value, style = {}) => (
    <div style={{ color: '#767676', ...style }}>{value}</div>
  );

  const renderSellerCell = (listing) => {
    const sellerNickname = listing?.sellerNickname || '-';
    return (
      <Label
        size="mini"
        title={sellerNickname}
        style={{
          display: 'block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sellerNickname}
      </Label>
    );
  };

  const renderStatusCell = (listing) => {
    const hasSubStatus =
      listing.status === 'paused' &&
      Array.isArray(listing.statusSubStatuses) &&
      listing.statusSubStatuses.length > 0;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Label
          size="mini"
          color={listing.status === 'active' ? 'green' : 'yellow'}
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {listing.status}
        </Label>
        {hasSubStatus ? (
          <Popup
            content="Ver en Mercado Libre por que esta pausada"
            mouseEnterDelay={0}
            mouseLeaveDelay={0}
            position="top center"
            trigger={
              <Icon
                name="info circle"
                style={{
                  alignSelf: 'center',
                  color: '#767676',
                  cursor: 'help',
                  flex: '0 0 auto',
                  lineHeight: 1,
                  margin: 0,
                  transform: 'translateY(-1px)',
                }}
              />
            }
          />
        ) : null}
      </div>
    );
  };

  const renderSellerStockCell = (itemId, listing) => {
    const isSaving = !!savingCells[cellKey(listing.id, 'availableQuantity')];
    const isRowSaving = !!savingListings[listing.id];
    const isSiblingSaving = !!savingItems[itemId];
    const isEditing = isEditingCell(listing.id, 'availableQuantity');
    const itemEditEnabled = !!rowEditEnabled?.[itemId];

    if (isKitListing(listing)) {
      return (
        <Popup
          content="El stock del kit se calcula automáticamente a partir de sus componentes."
          on={['hover', 'focus']}
          position="top center"
          trigger={
            <div
              tabIndex={0}
              aria-label="Stock calculado a partir de los componentes del kit"
              style={{ color: '#667085', cursor: 'help' }}
            >
              {getListingReportableStock(listing)}
            </div>
          }
        />
      );
    }

    if (isMlFullOnly(listing) || itemEditEnabled || isEditing) {
      return renderEditableCell(itemId, listing, 'availableQuantity');
    }

    const mercuryStock = getListingReportableStock(listing);
    const physicalStock = getSellerStock(listing);
    const marketplaceStock = getMarketplaceStock(listing);
    const showDrift = hasStockDrift(listing);
    const readContent = showDrift ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          lineHeight: 1.25,
          color: '#92400e',
        }}
      >
        <Icon
          name="warning sign"
          color="orange"
          style={{ margin: 0, flexShrink: 0 }}
          title={`MercuryCom ${mercuryStock} / ${getMarketplaceName(listing.marketplace)} ${marketplaceStock}`}
        />
        <span style={{ color: '#1f2937' }}>
          <strong>MercuryCom {mercuryStock}</strong>
          {' / '}
          {getMarketplaceName(listing.marketplace)} {marketplaceStock}
        </span>
      </div>
    ) : (
      <span>{mercuryStock}</span>
    );

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => beginEdit(listing, 'availableQuantity')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') beginEdit(listing, 'availableQuantity');
        }}
        style={{
          cursor: isSaving || isRowSaving ? 'default' : 'text',
          pointerEvents: isSaving || isRowSaving || isSiblingSaving ? 'none' : 'auto',
        }}
      >
        {readContent}
        {getListingSaleUnitQuantity(listing) > 1 ? (
          <div style={{ color: '#6B7280', fontSize: 11, marginTop: 2 }}>
            {physicalStock} unidades físicas
          </div>
        ) : null}
      </div>
    );
  };

  const renderFullStockCell = (listing) => {
    if (!isMlFullOnly(listing) && !isMlFullFlex(listing)) {
      return renderStaticCell('-');
    }
    return renderStaticCell(String(getFullStock(listing)), { color: '#1b1c1d' });
  };

  const renderLastComputedAtCell = (listing) => {
    const rawValue = listing?.lastComputedAt || null;
    const formattedValue = formatDateTime(rawValue);
    return <span style={{ color: '#555', fontSize: 12 }}>{formattedValue}</span>;
  };

  return (
    <>
      <Table.Body>
        {filteredItems.map((item, itemIndex) => {
        // Selection mode opens rows by default; an explicit row toggle takes precedence.
        const expanded = resolveExpanded(item);
        const itemEditEnabled = !!rowEditEnabled?.[item.id];

        return (
          <React.Fragment key={item.id}>
            <Table.Row>
              <Table.Cell
                textAlign="center"
                verticalAlign="middle"
                style={{
                  width: '50px',
                  padding: '5px',
                  position: 'relative',
                  ...(firstCopyGuidanceActive && itemIndex === 0 ? {
                    background: '#F5F3FF',
                    boxShadow: 'inset 0 0 0 2px rgba(124, 58, 237, 0.35)',
                    transition: 'background 180ms ease, box-shadow 180ms ease',
                  } : {}),
                }}
              >
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onChange={() => handleCheckboxChange(item)}
                  aria-label={`Seleccionar ${item.title || 'publicación'}`}
                />
                {firstCopyGuidanceActive && itemIndex === 0 ? (
                  <FirstCopyCoachmark onDismiss={onFirstCopyGuidanceDismiss} />
                ) : null}
              </Table.Cell>

              <Table.Cell style={{ textAlign: 'left', verticalAlign: 'middle', padding: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {item.pictureUrl && (
                    <Image
                      src={item.pictureUrl}
                      avatar
                      size="mini"
                      loading="lazy"
                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                    />
                  )}
                  <div>
                    {item.permalink ? (
                      <a href={item.permalink} target="_blank" rel="noopener noreferrer" style={{ color: '#4183c4', fontWeight: 'bold' }}>
                        {item.title}
                      </a>
                    ) : (
                      <span style={{ fontWeight: 'bold' }}>{item.title}</span>
                    )}
                    <div style={{ marginTop: 6 }}>
                      <Button size="mini" basic onClick={() => toggleExpanded(item)}>
                        {expanded ? 'Ocultar publicaciones' : `Publicaciones (${item.listings.length})`}
                      </Button>
                    </div>
                  </div>
                </div>
              </Table.Cell>

              <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle', padding: '10px' }}>
                {item.variationName || <Icon name="ban" />}
              </Table.Cell>

              <Table.Cell style={{ textAlign: 'left', verticalAlign: 'middle', padding: '10px' }}>{item.sku}</Table.Cell>

              <Table.Cell style={{ textAlign: 'left', verticalAlign: 'middle', padding: '10px' }}>${item.cost || '0.00'}</Table.Cell>

              <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle', padding: '10px' }}>
                <Label size="small" color={item.status === 'active' ? 'green' : 'yellow'}>
                  {item.status}
                </Label>
              </Table.Cell>

              <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle', padding: '10px' }}>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {getItemMarketplaces(item).map((marketplace) => (
                    <img
                      key={marketplace}
                      src={getMarketplaceIcon(marketplace)}
                      alt={marketplace}
                      title={marketplace}
                      style={{ width: '22px', height: '22px' }}
                    />
                  ))}
                </div>
              </Table.Cell>

              <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle', padding: '10px' }}>
                <Button
                  circular
                  color={itemEditEnabled ? 'green' : 'purple'}
                  size="mini"
                  icon={itemEditEnabled ? 'check' : 'edit'}
                  onClick={() => {
                    if (!expanded) toggleExpanded(item);
                    handleEditClick(item);
                  }}
                />
              </Table.Cell>
            </Table.Row>

            {expanded && (
              <Table.Row>
                <Table.Cell colSpan={8} style={{ background: '#fafafa', overflow: 'visible' }}>
                  <div style={{ display: 'grid', gap: 8, overflow: 'visible' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: detailGridTemplateColumns,
                        gap: 10,
                        alignItems: 'center',
                        padding: '0 8px',
                        color: '#767676',
                        fontSize: 12,
                        textTransform: 'uppercase',
                      }}
                    >
                      <span />
                      <span>Canal</span>
                      <span>SKU</span>
                      {packsEnabled ? (
                        <Popup
                          content="Muestra las unidades que se publican. Al editar el stock se ingresan las unidades físicas del producto."
                          on={['hover', 'focus']}
                          position="top center"
                          trigger={
                            <span tabIndex={0} style={{ cursor: 'help' }}>
                              Stock publicado <Icon name="info circle" />
                            </span>
                          }
                        />
                      ) : (
                        <span>Stock</span>
                      )}
                      <span>Stock Full</span>
                      <span>Precio</span>
                      <span>Seller</span>
                      <span>Estado</span>
                      <span>Catalogo</span>
                      <span>Actualizado</span>
                      {bulkPriceUpdateEnabled === true ? <span>Recibis</span> : null}
                      {bulkPriceUpdateEnabled === true ? <span>Margen</span> : null}
                      {bulkPriceUpdateEnabled === true ? <span>Margen %</span> : null}
                      {bulkPriceUpdateEnabled === true ? <span>Detalle</span> : null}
                      <span>Accion</span>
                    </div>
                    {item.listings.map((listing) => (
                      <div
                        key={`detail-${listing.id}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: detailGridTemplateColumns,
                          gap: 10,
                          alignItems: 'center',
                          border: '1px solid #eee',
                          borderRadius: 6,
                          padding: 8,
                          background: '#fff',
                          opacity: savingListings[listing.id] || savingItems[item.id] || deletingListingId === listing.id ? 0.6 : 1,
                          pointerEvents: savingListings[listing.id] || savingItems[item.id] ? 'none' : 'auto',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <Checkbox
                            checked={selectedListingIdSet.has(String(listing.id || '').trim())}
                            onChange={() => handleListingCheckboxChange(listing.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {savingListings[listing.id] || savingItems[item.id] ? (
                            <Icon name="spinner" loading style={{ marginLeft: 3 }} />
                          ) : (
                            <img src={getMarketplaceIcon(listing.marketplace)} alt={listing.marketplace} style={{ width: 22, height: 22 }} />
                          )}
                        </div>
                        <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
                          {listing.permalink ? (
                            <a href={listing.permalink} target="_blank" rel="noopener noreferrer" style={{ lineHeight: 1.45 }}>
                              {listing.title}
                            </a>
                          ) : (
                            <span style={{ lineHeight: 1.45 }}>{listing.title}</span>
                          )}
                          {getListingSaleUnitQuantity(listing) > 1 ? (
                            <Label size="mini" color="violet" basic>
                              Pack x{getListingSaleUnitQuantity(listing)}
                            </Label>
                          ) : null}
                          {item.kind === 'KIT' || listing.kind === 'KIT' ? (
                            <Label
                              size="mini"
                              color="violet"
                              basic
                              style={{
                                alignSelf: 'start',
                                justifySelf: 'start',
                                width: 'fit-content',
                                margin: 0,
                              }}
                            >
                              Kit
                            </Label>
                          ) : null}
                        </div>
                        {renderEditableCell(item.id, listing, 'sku')}
                        {renderSellerStockCell(item.id, listing)}
                        {renderFullStockCell(listing)}
                        {renderEditableCell(item.id, listing, 'price')}
                        {renderSellerCell(listing)}
                        {renderStatusCell(listing)}
                        {listing.isCatalog ? (
                          <Label size="mini" color="teal">
                            Catalogo
                          </Label>
                        ) : (
                          <Label size="mini">No catalogo</Label>
                        )}
                        <div style={{ whiteSpace: 'nowrap' }}>{renderLastComputedAtCell(listing)}</div>
                        {bulkPriceUpdateEnabled === true ? renderReceivedCell(listing) : null}
                        {bulkPriceUpdateEnabled === true ? renderMarginCell(listing) : null}
                        {bulkPriceUpdateEnabled === true ? renderMarginPercentCell(listing) : null}
                        {bulkPriceUpdateEnabled === true ? renderFinancialDetailCell(listing) : null}
                        {renderListingActionCell(item, listing)}
                      </div>
                    ))}
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </React.Fragment>
        );
        })}
      </Table.Body>
      <Modal open={!!pendingSkuSplit} size="small" closeOnDimmerClick={false}>
        <Modal.Header>
          {pendingSkuSplit?.code === 'EXISTING_SKU_REBIND_REQUIRES_CONFIRMATION'
            ? 'Vincular publicacion a un item existente'
            : 'Separar publicacion en un nuevo item'}
        </Modal.Header>
        <Modal.Content>
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ margin: 0 }}>
              {pendingSkuSplit?.message ||
                `Esta publicacion comparte item con otras publicaciones. Para cambiar su SKU de ${pendingSkuSplit?.currentSku || '-'} a ${pendingSkuSplit?.nextSku || '-'}, se creara un item nuevo con stock 0.`}
            </p>
            <p style={{ margin: 0, color: '#666' }}>
              {pendingSkuSplit?.code === 'EXISTING_SKU_REBIND_REQUIRES_CONFIRMATION'
                ? `La publicacion se agregara al item existente ${pendingSkuSplit?.targetItemTitle ? `"${pendingSkuSplit.targetItemTitle}"` : 'con ese SKU'}. Despues tendras que revisar o ajustar el stock de ese item si hace falta.`
                : 'El nuevo item arrancara con stock 0. Luego podras cargar stock manualmente si lo necesitas.'}
            </p>
            <Message
              info
              style={{ margin: 0 }}
              content={`Publicacion: ${pendingSkuSplit?.listing?.title || '-'}`}
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              pendingSkuSplit?.resolve?.(false);
              setPendingSkuSplit(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            color="blue"
            onClick={() => {
              pendingSkuSplit?.resolve?.(true);
              setPendingSkuSplit(null);
            }}
          >
            {pendingSkuSplit?.code === 'EXISTING_SKU_REBIND_REQUIRES_CONFIRMATION'
              ? 'Vincular al item existente'
              : 'Crear item con stock 0'}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal open={!!pendingVariationPriceChange} size="small" closeOnDimmerClick={false}>
        <Modal.Header>Cambiar el precio de todas las variaciones</Modal.Header>
        <Modal.Content>
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ margin: 0 }}>
              Esta publicación tiene {pendingVariationPriceChange?.variationCount || 'varias'} variaciones.
              Mercado Libre requiere que todas tengan el mismo precio.
            </p>
            <p style={{ margin: 0, color: '#666' }}>
              Al continuar, el precio se cambiará a {formatCurrency(pendingVariationPriceChange?.nextPrice)}
              {' '}en todas las variaciones de esta publicación.
            </p>
            <Message
              warning
              style={{ margin: 0 }}
              content={`Publicación: ${pendingVariationPriceChange?.listing?.title || '-'}`}
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              pendingVariationPriceChange?.resolve?.(false);
              setPendingVariationPriceChange(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            color="blue"
            onClick={() => {
              pendingVariationPriceChange?.resolve?.(true);
              setPendingVariationPriceChange(null);
            }}
          >
            Cambiar todos los precios
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal open={!!pendingDeleteListing} size="small" closeOnDimmerClick={false}>
        <Modal.Header>Borrar listing de MercuryCom</Modal.Header>
        <Modal.Content>
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ margin: 0 }}>
              Se va a borrar de MercuryCom el listing y no de {getMarketplaceName(pendingDeleteListing?.marketplace)}.
            </p>
            <Message
              warning
              style={{ margin: 0 }}
              content={`Publicacion: ${pendingDeleteListing?.title || '-'}`}
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setPendingDeleteListing(null)}
            disabled={!!deletingListingId}
          >
            Cancelar
          </Button>
          <Button
            color="red"
            icon
            labelPosition="left"
            onClick={confirmDeleteListing}
            loading={!!deletingListingId}
            disabled={!!deletingListingId}
          >
            <Icon name="trash" />
            Borrar
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default React.memo(TableContentComponent);
